/**
 * Typed fetch wrapper for `/api/v1/support/**` (hardening plan Phase
 * 3, 3c/Step 6/Step 7). Deliberately plain `fetch`, not
 * `@tanstack/react-query`'s cache-sharing machinery reused from the
 * operator SPA -- this console's data is cross-site and
 * support-role-scoped, a distinct enough cache domain that sharing
 * `@shared/api/*`'s client (built around per-site `useActiveSite`
 * assumptions this app doesn't have) would fight its own conventions
 * more than it would save. React Query itself (the library, not the
 * operator SPA's client) IS reused -- see `main.tsx`.
 */
import { getCurrentToken } from '@shared/auth/token-store';
import { getRuntimeConfig } from '../config/runtime-config';

export class SupportApiError extends Error {
  constructor(
    public status: number,
    public code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const { apiBaseUrl } = getRuntimeConfig();
  const token = getCurrentToken();
  const resp = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (!resp.ok) {
    let code: string | undefined;
    let message = `HTTP ${resp.status}`;
    try {
      const body = await resp.json();
      if (Array.isArray(body?.detail)) {
        // FastAPI's OWN 422 -- request validation that fails before any
        // handler runs -- carries `detail` as a list of
        // `{loc, msg, type}`, not the `{code, message}` object every
        // handler here raises. Read as an object it came out as a bare
        // "HTTP 422", which is exactly the answer the retire runbook
        // warns is misread: a missing or empty `reason` looks like "the
        // task failed the checks" when it means "fix the request". Name
        // the field and the complaint instead.
        code = 'validation_error';
        message = body.detail
          .map((d: { loc?: unknown[]; msg?: string }) => {
            const where = (d?.loc ?? []).filter((p) => p !== 'body').join('.');
            return `${where || 'request'}: ${d?.msg ?? 'invalid'}`;
          })
          .join('; ');
      } else {
        code = body?.detail?.code ?? body?.code;
        message = body?.detail?.message ?? body?.message ?? message;
      }
    } catch {
      // non-JSON error body -- keep the generic message
    }
    throw new SupportApiError(resp.status, code, message);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

// --- Types (mirror backend/api_server/api/support.py's pydantic models) ---

export type WorkerStatus = 'ready' | 'initialising' | 'stale' | 'unreachable';

export interface SupportIssueCounts {
  open: number;
  in_progress: number;
  resolved: number;
}

export interface SupportSiteHealth {
  site: string;
  /** `worker_unreachable` (2026-09-05): the site's WORKER did not answer
   *  the health command -- the site or the bus, not the broker. */
  status: 'ok' | 'unreachable' | 'not_configured' | 'worker_unreachable';
  cpu_percent: number | null;
  mem_percent: number | null;
  disk_percent: number | null;
  /** Why the sample is missing when `status` is not `ok` -- a short
   *  code plus the underlying error, e.g. "broker_unreachable:
   *  ConnectError". Null when the broker answered. */
  reason: string | null;
  /** How the answer was obtained. `worker` means the site's own worker
   *  read its broker (every Kubernetes site); the three percentages are
   *  null there by construction, not by failure. Absent from older
   *  api-servers. */
  source?: 'worker' | 'platform_http' | null;
  containers_ok?: number | null;
  containers_total?: number | null;
  /** Containers not running or unhealthy, by name. */
  not_ok?: string[] | null;
}

export interface SupportSiteHealthResponse {
  queried_at_ms: number;
  sites: SupportSiteHealth[];
}

/** Separate from `listSupportSites` on purpose: this one is a slow
 *  (~2s) fan-out to each site's broker, while the site list is a fast
 *  DB read. Bundling them made the Inventory table wait on every
 *  broker before it could paint anything. */
export function getSupportSiteHealth(): Promise<SupportSiteHealthResponse> {
  return request('/api/v1/support/site-health');
}

/** Per-site view of what a site is actually PUBLISHING on the platform
 *  bus, which is a different question from whether its worker is
 *  alive. `worker_status: 'ready'` + `silent: true` together are the
 *  runtime-wedge signature that otherwise only surfaces as a task
 *  failing at auction with "no bids". */
export interface SupportBusSiteHealth {
  site: string;
  silent: boolean;
  total_msgs_per_s: number;
  last_seen_age_s: number | null;
  window_s: number;
  /** Message rate per bus category (e.g. fleet_state, task_state). */
  categories: Record<string, number>;
  worker_status: string | null;
  worker_epoch: number | null;
}

export interface SupportBusResponse {
  queried_at_ms: number;
  sites: SupportBusSiteHealth[];
  /** Non-null when the bus observer is not running on this process. */
  note: string | null;
}

export function getSupportBusHealth(): Promise<SupportBusResponse> {
  return request('/api/v1/support/bus');
}

export interface SupportSiteSummary {
  site: string;
  label: string;
  worker_status: WorkerStatus;
  worker_epoch: number | null;
  deployment_manifest_version: string;
  certified_capabilities: string[];
  zenoh_router_endpoint: string | null;
  rmf_session_uri: string | null;
  worker_image_digest: string | null;
  broker_endpoint: string | null;
  issue_counts: SupportIssueCounts;
}

export interface SupportComponentSummary {
  site: string;
  component: string;
  status: 'healthy' | 'degraded' | 'unavailable';
  detail: string | null;
}

export interface SupportIssueOut {
  id: string;
  site: string | null;
  source: string;
  severity: string;
  title: string;
  fingerprint: string;
  reference: string;
  first_seen_ms: number;
  last_seen_ms: number;
  count: number;
  triage_state: 'open' | 'in_progress' | 'resolved';
  triage_updated_at_ms: number | null;
  started_by_email: string | null;
  resolved_at_ms: number | null;
  resolved_by_email: string | null;
  /** Scrubbed JSONB payload (richest per-issue evidence; shape varies
   *  by `source` — recovery proposal, task state, worker log record,
   *  or browser error). Null when the ingester recorded none. */
  detail: Record<string, unknown> | null;
  /** The coarse failure signature the backend derived from
   *  title/source/detail — the SAME derivation the AI analysis uses,
   *  so the UI can link to the exact Knowledge preview the assistant
   *  would be handed. Null when nothing matched. */
  derived_signature: string | null;
}

export interface SupportIssuesResponse {
  issues: SupportIssueOut[];
  next_cursor: string | null;
}

export interface SupportLogLine {
  at_ms: number;
  text: string;
}

export interface SupportLogsResponse {
  site: string;
  lines: SupportLogLine[];
}

export interface SupportRunbookStep {
  kind: string;
  description: string | null;
  service: string | null;
  verb: string | null;
  operation: string | null;
  precondition: string | null;
  postcondition: string | null;
}

export interface SupportRunbookSummary {
  id: string;
  title: string;
  description: string | null;
  trigger_signature: string | null;
  /** Secondary signatures this runbook also answers. Present since
   *  api-server 0.6.32; `undefined` from an older server. The AI panel
   *  has been fed these since the field existed and the deterministic
   *  suggestion list was not, so the two halves of this console
   *  disagreed about whether a runbook existed for the failure in
   *  front of the engineer. */
  also_triggered_by?: string[] | null;
  tier: string | null;
  /** The catalogue author's flag: is this runbook finished at all.
   *  Static and identical at every site. */
  available: boolean;
  /** Deployment shapes this runbook works on, when it is not all of
   *  them (`["compose"]` on the visualizer restart). */
  platforms?: string[] | null;
  /** Filled only by `listSupportRunbooks(site)`. `undefined` from an
   *  older server, and from the no-site call: treat that as "no
   *  constraint known", never as "not runnable". */
  runnable?: boolean | null;
  unavailable_reason?: string | null;
  steps: SupportRunbookStep[];
}

export interface SupportRunbooksResponse {
  runbooks: SupportRunbookSummary[];
  note: string | null;
}

export interface RunbookRunStepOut {
  index: number;
  kind: string;
  description: string | null;
  service: string | null;
  verb: string | null;
  operation: string | null;
  status: string;
  detail: string | null;
  started_at_ms: number | null;
  finished_at_ms: number | null;
}

export interface RunbookRunResponse {
  id: string;
  runbook_id: string;
  site: string;
  status: string;
  reason: string;
  requested_by: string;
  created_at_ms: number;
  steps: RunbookRunStepOut[];
}

export interface SupportAnalysisCandidate {
  runbook_id: string;
  title: string | null;
  evidence: string;
  confidence: string;
  prior_success_rate: string | null;
}

/** Why a `done` analysis produced zero candidates — distinguishes a
 *  genuine "no catalogued runbook fits" (null) from the failure
 *  modes that used to render identically to it.
 *
 *  `deadline_exceeded` is one of TWO shapes the server may report a
 *  cut-short run in (the other is the `deadline_exceeded` boolean on
 *  the result); the UI reads both and treats neither-present as "not
 *  cut short", so an older api-server keeps working unchanged. */
export type AnalysisFailureReason =
  | 'unparseable_output'
  | 'no_final_answer'
  | 'unknown_runbooks_only'
  | 'deadline_exceeded'
  | 'llm_request_timeout';

export interface SupportAnalysisResult {
  candidates: SupportAnalysisCandidate[];
  tool_calls_used: number;
  failure_reason: AnalysisFailureReason | null;
  /** The OpenRouter model that actually ran (runtime-configurable). */
  model: string | null;
  /** The failure signature whose knowledge block was assembled into
   *  the prompt; null when none could be derived from the issue. */
  signature_used: string | null;
  /** The model's own 2-5 sentence diagnosis (likely cause, evidence
   *  checked, next step). Worth showing even when `candidates` is
   *  empty -- on a young catalogue that is the common outcome. */
  assessment: string | null;
  /** The model's final text, <=4000 chars, present whenever it
   *  produced any content at all -- including the unparseable-prose
   *  case, which is exactly when a human still wants to read it. */
  raw_answer: string | null;
  /** Wall-clock time the run took. Optional: older api-servers omit
   *  it, and the UI simply says nothing about timing then. */
  elapsed_ms?: number | null;
  /** Tool-calling rounds the model actually used. Shown only inside
   *  the cut-short note, where it explains WHERE the run stopped --
   *  never as a standalone statistic (the triage block deliberately
   *  does not report plumbing). */
  rounds_used?: number | null;
  /** True when the run was stopped by its deadline rather than by the
   *  model finishing. The same fact may arrive as
   *  `failure_reason === 'deadline_exceeded'`; read both, treat
   *  missing as false. */
  deadline_exceeded?: boolean | null;
  /** The slowest single model call of the run, in milliseconds; 0
   *  when no call completed. Read ONLY into the two deadline texts,
   *  where it turns "it ran out of time" into "one model step took
   *  39s -- if Model thinking is on in Settings, turn it off;
   *  otherwise choose a faster model there": on 2026-09-03 a 90 s
   *  production run whose model steps took 24 s and 39 s (on the
   *  reasoning model an admin had switched to on 2026-09-02) reported
   *  only the former and sent the engineer nowhere.
   *  Optional because an older api-server omits it; the UI treats
   *  missing and 0 alike as "nothing to say". */
  llm_ms_max?: number | null;
}

/** What an analysis job was run against. Optional on the response
 *  because older api-servers, which could only analyse issues, omit
 *  it entirely. */
export interface SupportAnalysisTarget {
  kind: 'issue' | 'ticket';
  id: string;
}

/** One live step of a running analysis.
 *
 *  `kind` is deliberately a plain string, not a union: the server's
 *  own model says the vocabulary
 *  (`queued|catalogue|knowledge|round_started|tool_started|
 *  tool_finished|reasoning|answering|done` -- `reasoning`, "the model
 *  is thinking before it answers", arrived 2026-09-03 alongside the
 *  Model thinking setting) will grow, and a console that meets an
 *  unknown kind must render `text` and carry on rather than fail to
 *  parse a run that is otherwise fine. Nothing in this app branches on
 *  `kind` for that reason -- `reasoning` itself shipped with no change
 *  to any renderer, which is the rule working as intended.
 *
 *  `at_ms` is wall-clock epoch milliseconds, the same unit as the
 *  response's `created_at_ms`, so an elapsed offset is a subtraction. */
export interface SupportAnalysisProgressEntry {
  at_ms: number;
  kind: string;
  text: string;
}

export interface SupportAnalysisResponse {
  id: string;
  status: string;
  /** When the run was STARTED. On a cached response this is the
   *  ORIGINAL run's timestamp, not the moment of the click that got it
   *  back -- which is exactly what makes it usable as the result's
   *  age. */
  created_at_ms: number;
  result: SupportAnalysisResult | null;
  error: string | null;
  target?: SupportAnalysisTarget | null;
  /** Live progress so far, oldest first. Empty before the run starts,
   *  and absent entirely from an older api-server -- read it as `?? []`
   *  everywhere. */
  progress?: SupportAnalysisProgressEntry[];
  /** The assessment text as it streams in, while `status` is
   *  `running`. A preview -- capped and possibly mid-sentence -- never
   *  the thing to quote: once `status` is `done`, `result.assessment`
   *  is authoritative and this must stop being shown. */
  partial_assessment?: string | null;
  /** True when the response came from the server's finished-analysis
   *  cache rather than from a run this request started. Such a
   *  response is always `done` and always carries `result`.
   *
   *  Only the POST can report this: `GET /analyses/{id}` returns the
   *  same job with `cached: false`, because from its point of view
   *  nothing was served from a cache. That asymmetry is why the
   *  console holds on to the POST response rather than immediately
   *  polling over it -- see `useAnalysisJob`. */
  cached?: boolean;
}

// --- Calls ---

export function listSupportSites(): Promise<SupportSiteSummary[]> {
  return request('/api/v1/support/sites');
}

export function listSupportComponents(site?: string): Promise<SupportComponentSummary[]> {
  const qs = site ? `?site=${encodeURIComponent(site)}` : '';
  return request(`/api/v1/support/components${qs}`);
}

export function listSupportIssues(params: {
  site?: string;
  signature?: string;
  since?: number;
  cursor?: string;
  limit?: number;
}): Promise<SupportIssuesResponse> {
  const qs = new URLSearchParams();
  if (params.site) qs.set('site', params.site);
  if (params.signature) qs.set('signature', params.signature);
  if (params.since !== undefined) qs.set('since', String(params.since));
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  const s = qs.toString();
  return request(`/api/v1/support/issues${s ? `?${s}` : ''}`);
}

export function getSupportLogs(params: {
  site: string;
  query: string;
  since_ms?: number;
  until_ms?: number;
  limit?: number;
}): Promise<SupportLogsResponse> {
  const qs = new URLSearchParams({ site: params.site, query: params.query });
  if (params.since_ms !== undefined) qs.set('since_ms', String(params.since_ms));
  if (params.until_ms !== undefined) qs.set('until_ms', String(params.until_ms));
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  return request(`/api/v1/support/logs?${qs.toString()}`);
}

/** The catalogue. With a `site`, each entry also carries whether THAT
 *  site can run it and, when it cannot, the one-line reason. */
export function listSupportRunbooks(site?: string): Promise<SupportRunbooksResponse> {
  const qs = site ? `?site=${encodeURIComponent(site)}` : '';
  return request(`/api/v1/support/runbooks${qs}`);
}

export function runSupportRunbook(
  runbookId: string,
  body: { site: string; reason: string; failure_signature?: string },
): Promise<RunbookRunResponse> {
  return request(`/api/v1/support/runbooks/${encodeURIComponent(runbookId)}/run`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getSupportRunbookRun(runId: string): Promise<RunbookRunResponse> {
  return request(`/api/v1/support/runbooks/runs/${encodeURIComponent(runId)}`);
}

export function cancelSupportRunbookRun(runId: string): Promise<RunbookRunResponse> {
  return request(`/api/v1/support/runbooks/runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
  });
}

export function recordRunbookOutcome(
  runId: string,
  body: { resolved: boolean; note?: string },
): Promise<{ run_id: string; resolved: boolean; lesson_status: string }> {
  return request(`/api/v1/support/runbooks/runs/${encodeURIComponent(runId)}/outcome`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** The endpoint now accepts either target shape (`{issue_id}` or
 *  `{ticket_id}`), so this takes either -- a bare string stays
 *  `{issue_id}` for every existing caller.
 *
 *  `force` skips the server's finished-analysis cache and runs a fresh
 *  conversation: what "Re-analyze" sends. Omitted (not sent as
 *  `false`) when it is not set, so the wire body of an ordinary start
 *  is byte-for-byte what it always was.
 *
 *  Answers **200** with a finished, `cached: true` result when this
 *  target has been analysed before, and **202** when it actually
 *  started a run. `fetch` treats both as ok, so callers read
 *  `response.cached`/`response.status` rather than the HTTP code. */
export function createSupportAnalysis(
  target: string | { issue_id: string } | { ticket_id: string },
  opts: { force?: boolean } = {},
): Promise<SupportAnalysisResponse> {
  const body: Record<string, unknown> =
    typeof target === 'string' ? { issue_id: target } : { ...target };
  if (opts.force) body.force = true;
  return request('/api/v1/support/analyses', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getSupportAnalysis(jobId: string): Promise<SupportAnalysisResponse> {
  return request(`/api/v1/support/analyses/${encodeURIComponent(jobId)}`);
}

// --- Knowledge base (AI analyzer work) -----------------------------------------

/**
 * The 8 `rmf-task-supervisor` classification `kind` values
 * (`docker/task-supervisor/supervisor/classify.py` in nexsoss-rmf),
 * the same vocabulary a runbook's `trigger_signature` and a doctrine
 * file's front-matter `signature` use. Hardcoded here rather than
 * fetched -- there is no enumeration endpoint for it (it is a fixed,
 * rarely-changing set baked into the classifier), and the console
 * needs it purely to populate a picker.
 */
export const KNOWN_FAILURE_SIGNATURES = [
  'no_bids',
  'adapter_down',
  'door_adapter_down',
  'process_down',
  'robot_dropped',
  'robot_faulted',
  'task_stalled',
  'process_unhealthy',
] as const;

export interface KnowledgeLesson {
  id: number;
  runbook_id: string | null;
  site: string | null;
  body: string;
}

export interface OutcomeStatistic {
  runbook_id: string;
  site: string;
  assessed_attempts: number;
  successes: number;
  unassessed_attempts: number;
  last_success_ms: number | null;
  last_failure_ms: number | null;
}

export interface AssembledKnowledge {
  signature: string;
  doctrine_title: string | null;
  doctrine: string | null;
  lessons: KnowledgeLesson[];
  outcome_statistics: OutcomeStatistic[];
  knowledge_available: boolean;
}

export function previewKnowledge(signature: string, site?: string): Promise<AssembledKnowledge> {
  const qs = new URLSearchParams({ signature });
  if (site) qs.set('site', site);
  return request(`/api/v1/support/knowledge/preview?${qs.toString()}`);
}

export interface RecoveryLesson {
  id: number;
  failure_signature: string;
  runbook_id: string | null;
  site: string | null;
  body: string;
  status: 'pending' | 'approved' | 'rejected' | 'archived';
  source: 'auto_drafted' | 'manual' | 'merged';
  created_at_ms: number;
  created_by_sub: string;
  reviewed_by_sub: string | null;
  cited_count: number;
}

export function listLessons(params: { status?: string; signature?: string } = {}): Promise<RecoveryLesson[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.signature) qs.set('signature', params.signature);
  const s = qs.toString();
  return request(`/api/v1/support/lessons${s ? `?${s}` : ''}`);
}

export function createLesson(body: {
  failure_signature: string;
  body: string;
  runbook_id?: string;
  site?: string;
}): Promise<RecoveryLesson> {
  return request('/api/v1/support/lessons', { method: 'POST', body: JSON.stringify(body) });
}

export function reviewLesson(
  lessonId: number,
  reviewStatus: 'approved' | 'rejected' | 'archived',
): Promise<RecoveryLesson> {
  return request(`/api/v1/support/lessons/${lessonId}/review`, {
    method: 'POST',
    body: JSON.stringify({ status: reviewStatus }),
  });
}

/** The state an existing starter lesson was found in when the seed
 *  skipped it. `merged` means compaction folded it into another
 *  lesson, so the starter's text lives on under a different id. */
export type SeedSkipReason = 'approved' | 'pending' | 'rejected' | 'archived' | 'merged';

export interface SeedSkip {
  signature: string;
  reason: SeedSkipReason;
  /** The first few words of the starter's body. A signature now carries
   *  several starters, so this is what tells two skips on the same key
   *  apart. */
  body_prefix: string;
}

export interface SeedStarterLessonsResult {
  /** Failure signatures whose starter lesson was written by this call,
   *  WITH REPEATS: four new `task_stalled` starters are four entries. */
  seeded: string[];
  /** Failure signatures whose starter lesson already existed, likewise
   *  with repeats. */
  skipped: string[];
  /** One entry per skipped STARTER. It used to be a record keyed by
   *  signature, which reported one skip where several happened.
   *  Optional because older api-servers omit it; an older api-server
   *  may also still send the record shape, which is read as absent. */
  skipped_reasons?: SeedSkip[];
}

/** Writes the cross-site starter lessons (several per failure
 *  signature, distilled from the pilot's incidents). Idempotent: a
 *  starter whose exact (signature, body) exists in ANY state comes back
 *  in `skipped` (with that state in `skipped_reasons` where the server
 *  reports it) and is never re-created. platform_admin only -- the
 *  server enforces it; the UI merely hides the button. */
export function seedStarterLessons(): Promise<SeedStarterLessonsResult> {
  return request('/api/v1/support/lessons/seed', { method: 'POST' });
}

export interface LessonMergePlan {
  failure_signature: string;
  runbook_id: string | null;
  site_id: string | null;
  source_ids: number[];
  merged_body: string;
}

export interface CompactPreviewResult {
  outcome_rollup_applied: boolean;
  outcome_rollup: { folded: number; rollups_touched: number; pruned: number };
  lesson_merges_applied: false;
  proposed_merges: LessonMergePlan[];
}

export interface CompactApplyResult {
  outcome_rollup_applied: boolean;
  outcome_rollup: { folded: number; rollups_touched: number; pruned: number };
  lesson_merges_applied: true;
  merged_groups: number;
}

export function compactKnowledge(apply: false): Promise<CompactPreviewResult>;
export function compactKnowledge(apply: true): Promise<CompactApplyResult>;
export function compactKnowledge(apply: boolean): Promise<CompactPreviewResult | CompactApplyResult> {
  return request(`/api/v1/support/knowledge/compact?apply=${apply}`, { method: 'POST' });
}

// --- Issue resolution, durable runs, support audit ----------------------------

export type TriageState = 'open' | 'in_progress' | 'resolved';

export interface IssueTriageOut {
  issue_id: string;
  state: TriageState;
  updated_at_ms: number | null;
  started_at_ms: number | null;
  started_by_email: string | null;
  resolved_at_ms: number | null;
  resolved_by_email: string | null;
  note: string | null;
}

/** Single state endpoint rather than start/resolve/reopen verbs -- with
 *  three states those would be six routes each re-deriving which
 *  fields to stamp. */
export function setIssueTriageState(
  issueId: string,
  state: TriageState,
  note = '',
): Promise<IssueTriageOut> {
  return request(`/api/v1/support/issues/${encodeURIComponent(issueId)}/state`, {
    method: 'POST',
    body: JSON.stringify({ state, note }),
  });
}

/** Bulk form of `setIssueTriageState` for clearing a triage backlog —
 *  the server caps `ids` at 200 per call and audits each issue
 *  individually. Unknown ids come back in `missing` rather than
 *  failing the whole batch. */
export function bulkSetIssueState(
  ids: string[],
  state: TriageState,
): Promise<{ updated: number; missing: string[] }> {
  return request('/api/v1/support/issues/bulk_state', {
    method: 'POST',
    body: JSON.stringify({ ids, state }),
  });
}

export interface PastRun {
  run_id: string;
  runbook_id: string;
  site: string;
  failure_signature: string | null;
  reason: string;
  requested_by_email: string | null;
  created_at_ms: number;
  status: string;
  resolved: boolean | null;
}

export function listRunbookRuns(params: { awaiting_outcome?: boolean; site?: string } = {}): Promise<PastRun[]> {
  const qs = new URLSearchParams();
  if (params.awaiting_outcome) qs.set('awaiting_outcome', 'true');
  if (params.site) qs.set('site', params.site);
  const s = qs.toString();
  return request(`/api/v1/support/runbooks/runs${s ? `?${s}` : ''}`);
}

export interface SupportAuditEntry {
  audit_id: string;
  actor_sub: string;
  actor_email: string | null;
  actor_roles: string[];
  action: string;
  target: Record<string, unknown>;
  site: string | null;
  at_ms: number;
  outcome: string;
  reason: string | null;
}

export function listSupportAudit(
  params: {
    site?: string;
    action_prefix?: string;
    actor?: string;
    outcome?: string;
    since_ms?: number;
    until_ms?: number;
    limit?: number;
  } = {},
): Promise<{ entries: SupportAuditEntry[] }> {
  const qs = new URLSearchParams();
  if (params.site) qs.set('site', params.site);
  if (params.action_prefix) qs.set('action_prefix', params.action_prefix);
  if (params.actor) qs.set('actor', params.actor);
  if (params.outcome) qs.set('outcome', params.outcome);
  if (params.since_ms !== undefined) qs.set('since_ms', String(params.since_ms));
  if (params.until_ms !== undefined) qs.set('until_ms', String(params.until_ms));
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  const s = qs.toString();
  return request(`/api/v1/support/audit${s ? `?${s}` : ''}`);
}

/** `value` is what to send as `?actor=`; `label` is what to show.
 *  `sub_count > 1` means one person holds several ZITADEL subs. */
export interface SupportAuditActor {
  value: string;
  label: string;
  sub_count: number;
}

export interface SupportAuditFacets {
  actors: SupportAuditActor[];
  actions: string[];
  sites: string[];
  outcomes: string[];
}

export function getSupportAuditFacets(): Promise<SupportAuditFacets> {
  return request('/api/v1/support/audit/facets');
}

// --- Metrics -----------------------------------------------------------------

export interface MetricsIssuesBySite {
  site: string;
  open: number;
  in_progress: number;
  resolved: number;
  total: number;
}

export interface MetricsCount {
  key: string;
  count: number;
}

export interface MetricsHourBucket {
  hour: number;
  count: number;
}

export interface MetricsRunbookUsage {
  runbook_id: string;
  runs: number;
  succeeded: number;
  failed: number;
  fixed: number;
  did_not_fix: number;
  outcome_unrecorded: number;
}

export interface SupportMetricsResponse {
  since_ms: number;
  until_ms: number;
  issues_by_site: MetricsIssuesBySite[];
  issues_by_status: MetricsCount[];
  issues_by_severity: MetricsCount[];
  issues_by_hour_utc: MetricsHourBucket[];
  runbook_usage: MetricsRunbookUsage[];
}

export function getSupportMetrics(
  params: { since_ms?: number; until_ms?: number } = {},
): Promise<SupportMetricsResponse> {
  const qs = new URLSearchParams();
  if (params.since_ms !== undefined) qs.set('since_ms', String(params.since_ms));
  if (params.until_ms !== undefined) qs.set('until_ms', String(params.until_ms));
  const s = qs.toString();
  return request(`/api/v1/support/metrics${s ? `?${s}` : ''}`);
}

// --- Tickets (hardening plan Phase 3, 3g) ------------------------------------
//
// These live under `/support/**` rather than the operator tier's
// `/sites/{site}/tickets` because this app talks ONLY to the
// support-role api-server process, which does not mount the operator
// router at all. Before these existed a filed ticket was unreachable
// from this console entirely -- a topology gap, not a UI one.

export type TicketState = 'new' | 'triaging' | 'waiting_on_user' | 'resolved' | 'closed';

export interface SupportTicketOut {
  ticket_id: string;
  site: string;
  requester_sub: string;
  requester_display_name: string | null;
  title: string;
  body: string;
  state: TicketState;
  severity: string;
  assignee_sub: string | null;
  created_at_ms: number;
  updated_at_ms: number;
  resolved_at_ms: number | null;
  external_ref: string | null;
  unconfirmed_link_count: number;
}

export interface SupportTicketLink {
  link_id: number;
  kind: 'issue' | 'alert';
  target_id: string;
  title: string | null;
  confidence: number;
  linked_by: string;
  confirmed_at_ms: number | null;
}

export interface SupportTicketComment {
  id: number;
  author_sub: string;
  author_display_name: string | null;
  body: string;
  visibility: string;
  created_at_ms: number;
}

export interface SupportTicketDetail extends SupportTicketOut {
  context_snapshot: Record<string, unknown>;
  links: SupportTicketLink[];
  comments: SupportTicketComment[];
}

export function listSupportTickets(
  params: { site?: string; state?: TicketState } = {},
): Promise<{ tickets: SupportTicketOut[] }> {
  const qs = new URLSearchParams();
  if (params.site) qs.set('site', params.site);
  if (params.state) qs.set('state', params.state);
  const s = qs.toString();
  return request(`/api/v1/support/tickets${s ? `?${s}` : ''}`);
}

export function getSupportTicket(ticketId: string): Promise<SupportTicketDetail> {
  return request(`/api/v1/support/tickets/${encodeURIComponent(ticketId)}`);
}

export function updateSupportTicket(
  ticketId: string,
  body: { state?: TicketState; assignee_sub?: string },
): Promise<SupportTicketOut> {
  return request(`/api/v1/support/tickets/${encodeURIComponent(ticketId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function addSupportTicketComment(
  ticketId: string,
  body: { body: string; visibility?: 'internal' | 'requester_visible' },
): Promise<{ id: number; created_at_ms: number }> {
  return request(`/api/v1/support/tickets/${encodeURIComponent(ticketId)}/comments`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function confirmSupportTicketLink(ticketId: string, linkId: number): Promise<void> {
  return request(
    `/api/v1/support/tickets/${encodeURIComponent(ticketId)}/links/${linkId}/confirm`,
    { method: 'POST' },
  );
}

export function rejectSupportTicketLink(ticketId: string, linkId: number): Promise<void> {
  return request(`/api/v1/support/tickets/${encodeURIComponent(ticketId)}/links/${linkId}`, {
    method: 'DELETE',
  });
}

// --- Orphaned-task retire (platform_admin, admin API) ------------------------
//
// THE ONLY `/api/v1/admin/**` CALLS IN THIS APP, and they are not proxied
// through the support container. `apiBaseUrl` is Envoy's front door,
// which already routes `/api/v1/admin/` to `nexsoss-api-server-admin`
// behind edge-authz's platform_admin check, and every deployed
// `CORS_ALLOWED_ORIGINS` lists this console's origin for all three
// api-server roles. So the same `request()` and the same bearer token
// reach it; a support_l2/l3 token is refused at the edge, and the UI
// never offers these to one (`RetireOrphanedTask.tsx`). Mirrors
// `backend/api_server/api/admin.py`'s `OrphanTaskEvidence`/`RetiredTask`.

export interface TaskControlOutcome {
  command_id: string;
  capability: string;
  /** `outcome_unknown` on a `task.cancel` is runbook point 3. */
  state: string;
  submitted_at_ms: number;
  state_changed_at_ms: number;
  outcome_message: string | null;
  submitted_by_sub: string;
}

export interface OrphanTaskEvidence {
  site_id: string;
  task_id: string;
  /** False: the bus could not be asked, and every projection field is
   *  UNKNOWN (null) -- never read that as "no record". */
  bus_available: boolean;
  bus_error: string | null;
  /** Replies the task-state read got; null when the bus was never asked.
   *  0 with `projected: false` is "nobody returned a record" -- an absent
   *  key and a lost storage route look the same. */
  bus_replies: number | null;
  projected: boolean;
  state: string | null;
  terminal: boolean;
  category: string | null;
  assigned_fleet: string | null;
  assigned_robot: string | null;
  projected_at_ms: number | null;
  projector_epoch: number | null;
  worker_status: string;
  worker_epoch: number | null;
  worker_last_heartbeat_ms: number | null;
  /** null when either epoch is unknown. `true` does NOT clear a task: a
   *  fleet-adapter-only restart leaves the worker's epoch unchanged. */
  epoch_matches_live_worker: boolean | null;
  /** The assigned robot's own projected state. False = unknown (no
   *  robot, unreadable, nothing cached); the next three are then null. */
  robot_state_available: boolean;
  robot_task_id: string | null;
  robot_state_at_ms: number | null;
  robot_executing_this_task: boolean | null;
  /**
   * -- WS3 (api-server 0.6.35). `robot_executing_this_task` above had NO
   * AGE TEST, so a robot whose adapter died ten minutes ago produced a
   * confident true or false. These carry the honest answer.
   *
   * `held` is the verdict -- `held` / `not_held` / `unknown` -- and
   * `unknown` covers a stale state, a failed read, and a task with no
   * robot. `robot_state_age_ms` is the number behind it.
   */
  robot_state_age_ms?: number | null;
  held?: 'held' | 'not_held' | 'unknown' | null;
  /** The lift the robot is in or boarding, and on what evidence. A task
   *  cancelled in a car leaves the robot parked inside holding the lift,
   *  so this DISABLES the end-held controls rather than warning. */
  robot_lift?: string | null;
  robot_lift_basis?: 'session' | 'standing_request' | 'cabin_pose' | null;
  /** RMF's raw status and the attention object, so the console can tell
   *  an `active` task that needs a human from one that is merely busy. */
  rmf_status?: string | null;
  attention?: { rmf_status: string; since_ms: number; reason?: string | null } | null;
  recent_controls: TaskControlOutcome[];
  at_ms: number;
}

export interface EndHeldTask {
  site_id: string;
  task_id: string;
  action: string;
  command: Record<string, unknown>;
  ownership: Record<string, unknown>;
}

export interface RetiredTask {
  site_id: string;
  task_id: string;
  previous_state: string;
  previous_projected_at_ms: number | null;
  previous_projector_epoch: number | null;
  retired_by: string | null;
  reason: string;
  record: Record<string, unknown>;
  at_ms: number;
}

/** Read-only; api-server 0.6.34+. An older server answers 404. */
export function getOrphanTaskEvidence(site: string, taskId: string): Promise<OrphanTaskEvidence> {
  return request(
    `/api/v1/admin/sites/${encodeURIComponent(site)}/tasks/${encodeURIComponent(taskId)}/retire-evidence`,
  );
}

/** `reason` is sent exactly as given -- it is the audit row's "why".
 *  422 = malformed request (missing/empty/over-long reason), 409
 *  `task_already_terminal` = the task has ended, 404
 *  `task_not_projected`, 503 `bus_unavailable` = nothing was touched. */
export function retireOrphanedTask(site: string, taskId: string, reason: string): Promise<RetiredTask> {
  return request(
    `/api/v1/admin/sites/${encodeURIComponent(site)}/tasks/${encodeURIComponent(taskId)}/retire`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

/**
 * Cancel or kill a task RMF is STILL EXECUTING behind a stale record.
 *
 * NOT a retire, and the distinction is the design: `retire` writes a
 * synthetic terminal record onto the task key and is for a task nothing
 * owns; this one never touches the key -- it publishes the same
 * `task.cancel`/`task.kill` a console button publishes and lets RMF
 * terminalise the task. On 2026-09-15 there was no such verb, so three
 * support attempts at 13:10 and 13:14 were refused with no alternative.
 *
 * api-server 0.6.35+; an older server answers 404. 409
 * `task_ownership_unknown` / `task_not_held` / `lift_state_unreadable` /
 * `robot_in_lift` all mean NOTHING was sent.
 */
export function endHeldTask(
  site: string,
  taskId: string,
  action: 'cancel' | 'kill',
  reason: string,
): Promise<EndHeldTask> {
  return request(
    `/api/v1/admin/sites/${encodeURIComponent(site)}/tasks/${encodeURIComponent(taskId)}/end-held`,
    { method: 'POST', body: JSON.stringify({ action, reason }) },
  );
}
